/**
 * Preview Tab Component
 * Live form preview with Design/Admin/Edit/View modes and
 * Form / Sample Data / Live Data sub-tabs.
 */

import React, { useState } from 'react';
import { useUIStore } from '@atlas-forms/state-react';
import type { FormSchema, OperatingMode as FormOperatingMode } from '@atlas-forms/types-js';
import { FormRenderer, FormThemeProvider } from '@atlas-forms/player-components-react';
import { TrustLevelBadge } from '@atlas-forms/controls-styling-schema-js';
import '../../styles/tabs/PreviewTab.css';

type DataSource = 'form' | 'sample' | 'live';

export interface PreviewTabProps {
  schema: FormSchema;
}

/**
 * Preview Tab - Live form preview
 */
export const PreviewTab: React.FC<PreviewTabProps> = ({ schema }) => {
  const { theme } = useUIStore();
  const isDark = theme === 'dark';
  const [previewMode, setPreviewMode] = useState<FormOperatingMode>('edit');
  const [dataSource, setDataSource] = useState<DataSource>('form');
  const [submitted, setSubmitted] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [submittedValues, setSubmittedValues] = useState<Record<string, any> | null>(null);
  const [formKey, setFormKey] = useState(0);

  // Editable sample data (initialised from schema metadata)
  const [sampleDataText, setSampleDataText] = useState<string>(() => {
    const sd = (schema.metadata as any)?.sampleData;
    if (!sd) return '{}';
    return typeof sd === 'string' ? sd : JSON.stringify(sd, null, 2);
  });
  const [sampleDataError, setSampleDataError] = useState<string | null>(null);

  // Parse sample data text
  let parsedSampleData: Record<string, any> | undefined;
  try { parsedSampleData = sampleDataText ? JSON.parse(sampleDataText) : undefined; } catch { parsedSampleData = undefined; }

  const handleModeChange = (mode: FormOperatingMode) => {
    setPreviewMode(mode);
    setPreviewError(null);
    setSubmitted(false);
  };

  const handleDataSourceChange = (src: DataSource) => {
    setDataSource(src);
    setSubmitted(false);
    setPreviewError(null);
    setFormKey((k) => k + 1);
  };

  const handleSubmit = async (values: Record<string, any>) => {
    setSubmittedValues(values);
    setSubmitted(true);
    setPreviewError(null);
  };

  const applySampleData = () => {
    try {
      JSON.parse(sampleDataText);
      setSampleDataError(null);
      setSubmitted(false);
      setFormKey((k) => k + 1);
    } catch {
      setSampleDataError('Invalid JSON — fix the syntax and try again.');
    }
  };

  const initialValues = dataSource === 'sample' ? parsedSampleData : undefined;

  const subTabStyle = (active: boolean): React.CSSProperties => ({
    padding: '5px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
    cursor: 'pointer', border: 'none', transition: 'all 0.15s',
    background: active
      ? isDark ? 'rgba(34,197,94,0.15)' : 'rgba(22,163,74,0.08)'
      : 'transparent',
    color: active
      ? isDark ? '#22c55e' : '#16a34a'
      : isDark ? '#64748b' : '#94a3b8',
    outline: active
      ? `1px solid ${isDark ? 'rgba(34,197,94,0.3)' : '#bae6fd'}`
      : '1px solid transparent',
  });

  return (
    <div className="af-preview-tab" data-theme={theme}>
      <div className="af-preview-tab__controls">
        {/* Data source (left) + Mode selector (right) in one row */}
        <div className="af-preview-tab__controls-row">
          {/* Data source sub-tabs — left */}
          <div className="af-preview-tab__data-selector">
            <span className="af-preview-tab__label">Data:</span>
            <button type="button" style={subTabStyle(dataSource === 'form')} onClick={() => handleDataSourceChange('form')}>
              Form
            </button>
            <button type="button" style={subTabStyle(dataSource === 'sample')} onClick={() => handleDataSourceChange('sample')}>
              Sample Data
            </button>
            <button type="button" style={subTabStyle(dataSource === 'live')} onClick={() => handleDataSourceChange('live')}>
              Live Data
            </button>
          </div>

          {/* Mode selector — right */}
          <div className="af-preview-tab__mode-selector">
            <span className="af-preview-tab__label">Mode:</span>
            {(['design', 'admin', 'edit', 'view'] as FormOperatingMode[]).map((mode) => (
              <button
                key={mode}
                onClick={() => handleModeChange(mode as FormOperatingMode)}
                className={`af-preview-tab__mode-btn ${previewMode === mode ? 'af-preview-tab__mode-btn--active' : ''}`}
              >
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>
            ))}
            {/* Trust Level Badge — show security status */}
            {(schema.metadata as any)?.trustLevel !== undefined && (
              <TrustLevelBadge level={(schema.metadata as any).trustLevel} showLabel={true} />
            )}
          </div>
        </div>
      </div>

      {/* Sample data editor (shown below controls bar when dataSource === 'sample') */}
      {dataSource === 'sample' && (
        <div style={{
          padding: '10px 16px',
          background: isDark ? 'rgba(15,23,42,0.6)' : '#f8fafc',
          borderBottom: `1px solid ${isDark ? 'rgba(51,65,85,0.4)' : '#e5e7eb'}`,
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: isDark ? '#64748b' : '#94a3b8' }}>
              Sample Data (JSON)
            </span>
            <button
              type="button"
              onClick={applySampleData}
              style={{
                padding: '3px 12px', borderRadius: 5, border: 'none', fontSize: 11, fontWeight: 700,
                background: isDark ? '#22c55e' : '#16a34a', color: '#fff', cursor: 'pointer',
              }}
            >Apply</button>
            {sampleDataError && (
              <span style={{ fontSize: 11, color: '#f87171' }}>{sampleDataError}</span>
            )}
          </div>
          <textarea
            value={sampleDataText}
            onChange={(e) => { setSampleDataText(e.target.value); setSampleDataError(null); }}
            rows={4}
            spellCheck={false}
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '8px 10px', borderRadius: 6, resize: 'vertical',
              fontSize: 12, fontFamily: 'monospace', lineHeight: 1.5,
              background: isDark ? 'rgba(2,6,23,0.8)' : '#fff',
              border: `1px solid ${sampleDataError ? '#f87171' : (isDark ? 'rgba(51,65,85,0.5)' : '#cbd5e1')}`,
              color: isDark ? '#94a3b8' : '#374151',
              outline: 'none',
            }}
          />
        </div>
      )}

      <div className="af-preview-tab__content">
        {schema.controls.length === 0 ? (
          <div className="af-preview-tab__empty-state">
            <p>No controls to preview</p>
            <p className="af-preview-tab__empty-hint">Add controls from the Controls tab to see preview</p>
          </div>
        ) : dataSource === 'live' ? (
          <div style={{ padding: 24 }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: isDark ? '#94a3b8' : '#64748b', marginTop: 0 }}>
              Live Data
            </p>
            {submittedValues ? (
              <>
                <p style={{ fontSize: 12, color: isDark ? '#64748b' : '#94a3b8', margin: '0 0 10px' }}>
                  Last submitted values:
                </p>
                <pre style={{
                  margin: 0, padding: '12px 14px', borderRadius: 8, fontSize: 12,
                  fontFamily: 'monospace', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                  background: isDark ? 'rgba(2,6,23,0.8)' : '#f8fafc',
                  border: `1px solid ${isDark ? 'rgba(51,65,85,0.4)' : '#e5e7eb'}`,
                  color: isDark ? '#94a3b8' : '#374151',
                }}>
                  {JSON.stringify(submittedValues, null, 2)}
                </pre>
                <button
                  type="button"
                  onClick={() => setSubmittedValues(null)}
                  style={{ marginTop: 10, padding: '5px 14px', borderRadius: 6, border: `1px solid ${isDark ? 'rgba(51,65,85,0.5)' : '#e5e7eb'}`, background: 'transparent', color: isDark ? '#64748b' : '#94a3b8', fontSize: 12, cursor: 'pointer' }}
                >Clear</button>
              </>
            ) : (
              <p style={{ fontSize: 12, color: isDark ? '#475569' : '#94a3b8' }}>
                Switch to <strong>Form</strong> data, fill in the form and click Submit — the values will appear here.
              </p>
            )}
          </div>
        ) : (
          <div className="af-preview-tab__preview-wrapper">
            <div className="af-preview-tab__form-container">
              <div className="af-preview-tab__form-header">
                <h2>{schema.metadata.title}</h2>
                {schema.metadata.description && (
                  <p>{schema.metadata.description}</p>
                )}
              </div>

              <div className="af-preview-tab__form-content">
                {submitted ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '48px 24px', textAlign: 'center' }}>
                    <div style={{ fontSize: 40 }}>✓</div>
                    <p style={{ fontSize: 16, fontWeight: 700, color: isDark ? '#4ade80' : '#16a34a', margin: 0 }}>
                      Preview submitted!
                    </p>
                    <p style={{ fontSize: 14, color: isDark ? '#94a3b8' : '#64748b', margin: 0 }}>
                      No data was saved — this is a designer preview.
                    </p>
                    <button
                      type="button"
                      onClick={() => { setSubmitted(false); setFormKey((k) => k + 1); }}
                      style={{
                        marginTop: 8, padding: '8px 20px', borderRadius: 8, border: 'none',
                        background: isDark ? 'rgba(34,197,94,0.15)' : '#16a34a',
                        color: isDark ? '#22c55e' : '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      Reset Form
                    </button>
                  </div>
                ) : (
                  <FormThemeProvider theme={theme as 'dark' | 'light'}>
                    <FormRenderer
                      key={`preview-${dataSource}-${formKey}`}
                      schema={schema as any}
                      mode={previewMode}
                      initialValues={initialValues}
                      onSubmit={handleSubmit}
                      onClose={() => {}}
                    />
                  </FormThemeProvider>
                )}
              </div>

              {previewError && (
                <div className="af-preview-tab__error">
                  {previewError}
                </div>
              )}
            </div>

            <div className="af-preview-tab__info">
              <h3>Preview Information</h3>
              <ul>
                <li><strong>Mode:</strong> {previewMode}</li>
                <li><strong>Data:</strong> {dataSource}</li>
                <li><strong>Controls:</strong> {schema.controls.length}</li>
                <li><strong>Sections:</strong> {schema.sections?.length || 0}</li>
                <li><strong>Version:</strong> {schema.version}</li>
              </ul>

              <h3>Mode Details</h3>
              <ul>
                {previewMode === 'design' && (
                  <>
                    <li>Design/layout mode</li>
                    <li>Controls shown in edit state</li>
                  </>
                )}
                {previewMode === 'edit' && (
                  <>
                    <li>Full editing capability</li>
                    <li>All controls are enabled</li>
                    <li>Validation enforced on submit</li>
                  </>
                )}
                {previewMode === 'view' && (
                  <>
                    <li>Read-only mode</li>
                    <li>No editing possible</li>
                    <li>Display-only controls visible</li>
                  </>
                )}
                {previewMode === 'admin' && (
                  <>
                    <li>Administrative mode</li>
                    <li>All fields visible and editable</li>
                    <li>Advanced options shown</li>
                  </>
                )}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

PreviewTab.displayName = 'PreviewTab';
